<template>
  <div id="contenedor-exportar-excel-tablero-expedientes">
    <v-list-item
      :disabled="estaCargando || resultados.length === 0"
      @click="generarExcelTableroExpedientes"
      link>
      <v-list-item-icon class="mx-2">
        <v-progress-circular
          v-if="estaCargando"
          size="20"
          width="2"
          indeterminate
          color="primary">
        </v-progress-circular>
        <v-icon v-else> mdi-file-excel </v-icon>
      </v-list-item-icon>
      <v-list-item-content>
        <v-list-item-title> Exportar Excel </v-list-item-title>
      </v-list-item-content>
    </v-list-item>
  </div>
</template>

<script>
import mixGenerarExcel from '@/helpers/mixGenerarExcel';
import CATALOGO_EXPEDIENTE from '@/graphql/querys/administracion/catalogos/expedientes/catalogoExpediente.gql';
import TABLERO_EXPEDIENTE_DETALLE from '@/graphql/querys/administracion/tableroExpedientes/tableroExpedienteDetalle.gql';
import CONTRATO_CANTIDADES_PARA_EXCEL from '@/graphql/querys/administracion/tableroExpedientes/contratoCantidadesParaExcel.gql';

const filaTitulos = 3;
const filaInicioDatos = filaTitulos + 1;
const altoFilaEncabezados = 409;
const anchoColumnasCatalogo = 5;

const NOMBRES_ETAPA = {
  1: 'Antes',
  2: 'Durante',
  3: 'Despues',
};

const COLUMNAS_FIJAS = [
  { value: 'Fecha de Contrato', nombreColumna: 'fecha', orden: 1 },
  { value: 'Contrato', nombreColumna: 'numero', orden: 2 },
  { value: 'Objeto', nombreColumna: 'objeto', orden: 3 },
  { value: 'Contratista', nombreColumna: 'contratista', orden: 4 },
  { value: 'Residente', nombreColumna: 'responsable', orden: 5 },
  { value: 'Documentos', nombreColumna: 'total', orden: 6 },
  { value: 'No Aplican', nombreColumna: 'noAplicables', orden: 7 },
  { value: 'Aplican', nombreColumna: 'aplican', orden: 8 },
  { value: 'Integrados', nombreColumna: 'integrados', orden: 9 },
  { value: 'Faltantes', nombreColumna: 'faltantes', orden: 10 },
  { value: 'Convenios Capturados', nombreColumna: 'conveniosCapturados', orden: 11 },
  { value: 'Estimaciones Capturadas', nombreColumna: 'estimacionesCapturadas', orden: 12 },
];

export default {
  name: 'ExcelTableroExpedientes',
  mixins: [mixGenerarExcel],
  props: {
    resultados: {
      type: Array,
      required: true,
    },
    filtros: {
      type: Object,
      required: true,
    },
  },
  apollo: {
    catalogoExpediente: {
      query: CATALOGO_EXPEDIENTE,
    },
  },
  data() {
    return {
      estaCargando: false,
      nombreDocumento: 'ESTATUS DETALLADO DE EXPEDIENTES',
      datosExportacion: [],
      estiloHeader: {
        font: {
          bold: true,
        },
        alignment: {
          horizontal: 'center',
          vertical: 'center',
          wrapText: true,
        },
      },
    };
  },
  methods: {
    async generarExcelTableroExpedientes() {
      this.estaCargando = true;
      try {
        this.datosExportacion = await this.cargarDatosExportacion();
        await this.mixGenerarExcel(this.crearExcelTableroExpedientes);
      } catch (error) {
        this.mixMensajeError(error);
      }
      this.estaCargando = false;
    },
    async cargarDatosExportacion() {
      if (!this.catalogoExpediente || this.catalogoExpediente.length === 0) {
        const catalogoResponse = await this.$apollo.query({
          query: CATALOGO_EXPEDIENTE,
          fetchPolicy: 'network-only',
        });
        this.catalogoExpediente = catalogoResponse.data.catalogoExpediente || [];
      }

      const detallePorContrato = await Promise.all(
        this.resultados.map(async (contrato) => {
          const [detalleResponse, cantidadesResponse] = await Promise.all([
            this.$apollo.query({
              query: TABLERO_EXPEDIENTE_DETALLE,
              variables: { idContrato: contrato.id },
              fetchPolicy: 'network-only',
            }),
            this.$apollo.query({
              query: CONTRATO_CANTIDADES_PARA_EXCEL,
              variables: { id: contrato.id },
              fetchPolicy: 'network-only',
            }),
          ]);

          const detalle = detalleResponse.data.tableroExpedienteDetalle || {};
          const contratoCantidades = cantidadesResponse.data.contrato || {};

          return {
            contrato,
            documentos: detalle.documentos || [],
            conveniosCapturados: (contratoCantidades.convenios || []).length,
            estimacionesCapturadas: (contratoCantidades.estimaciones || []).length,
          };
        }),
      );

      return detallePorContrato;
    },
    getResponsablePrincipal(contrato) {
      const responsable = (contrato.responsables || [])
        .find(({ principal }) => principal);
      return responsable ? responsable.funcionario.nombre : '';
    },
    claveColumnaCatalogo(documentoCatalogo) {
      return `doc_${documentoCatalogo.id}`;
    },
    getValorCeldaCatalogo(documentos, idDocumento) {
      const documentosDelTipo = documentos.filter(
        (documento) => documento.idDocumento === idDocumento,
      );

      if (documentosDelTipo.length === 0) return 'NE';

      const todosNoAplican = documentosDelTipo.every((documento) => (
        documento.estado === 'NO_APLICA' || documento.aplica === false
      ));
      if (todosNoAplican) return 'NA';

      return documentosDelTipo.filter((documento) => (
        documento.estado === 'INTEGRADO'
      )).length;
    },
    celdaCatalogo(valor) {
      if (typeof valor === 'number') return this.celdaNumero(valor);
      return { value: valor };
    },
    celdaNumero(value, estiloExtra = {}) {
      return {
        value: Number(value) || 0,
        estilo: {
          numberFormat: '0',
          ...estiloExtra,
        },
      };
    },
    celdaTotal(value) {
      if (typeof value === 'string') {
        return { value, estilo: { font: { bold: true } } };
      }
      return this.celdaNumero(value, { font: { bold: true } });
    },
    valorNumericoCelda(celda) {
      if (celda && typeof celda === 'object') return Number(celda.value) || 0;
      return Number(celda) || 0;
    },
    sumarCampoFilas(campo) {
      return this.filasDatos.reduce((total, filaDato) => (
        total + this.valorNumericoCelda(filaDato[campo])
      ), 0);
    },
    estiloHeaderCatalogo() {
      return {
        font: { bold: true },
        alignment: {
          horizontal: 'center',
          vertical: 'center',
          wrapText: true,
          textRotation: 90,
        },
      };
    },
  },
  computed: {
    getCatalogoOrdenado() {
      if (!this.catalogoExpediente) return [];
      return this.catalogoExpediente
        .slice()
        .sort((a, b) => (a.etapa - b.etapa) || (a.orden - b.orden));
    },
    headersColumnas() {
      const columnasCatalogo = this.getCatalogoOrdenado.map((documento, index) => ({
        value: documento.descripcion,
        nombreColumna: this.claveColumnaCatalogo(documento),
        orden: COLUMNAS_FIJAS.length + index + 1,
        etapa: documento.etapa,
        idDocumento: documento.id,
      }));
      return [...COLUMNAS_FIJAS, ...columnasCatalogo];
    },
    getHeaders() {
      return this.headersColumnas.map((header, index) => {
        if (index < COLUMNAS_FIJAS.length) return header.value;
        return {
          value: header.value,
          estilo: this.estiloHeaderCatalogo(),
        };
      });
    },
    getColumnas() {
      return this.headersColumnas.map((columna) => columna.nombreColumna);
    },
    totalColumnas() {
      return this.headersColumnas.length || COLUMNAS_FIJAS.length;
    },
    getConfiguracionExcel() {
      const anchoColumnas = [];
      const totalCatalogo = this.getCatalogoOrdenado.length;

      if (totalCatalogo > 0) {
        anchoColumnas.push({
          columna: COLUMNAS_FIJAS.length + 1,
          to: COLUMNAS_FIJAS.length + totalCatalogo,
          size: anchoColumnasCatalogo,
        });
      }

      return {
        altoFilas: [{ fila: filaTitulos, size: altoFilaEncabezados }],
        anchoColumnas,
      };
    },
    filasDatos() {
      return this.datosExportacion.map(({
        contrato,
        documentos,
        conveniosCapturados,
        estimacionesCapturadas,
      }) => {
        const fila = {
          fecha: this.$moment(contrato.fecha).format('DD/MM/YYYY'),
          numero: contrato.numero,
          objeto: contrato.meta,
          contratista: contrato.contratista ? contrato.contratista.nombreRazonSocial : '',
          responsable: this.getResponsablePrincipal(contrato),
          total: this.celdaNumero(contrato.totalDocumentos),
          noAplicables: this.celdaNumero(contrato.noAplicables),
          aplican: this.celdaNumero(contrato.documentosAplicables),
          integrados: this.celdaNumero(contrato.integrados),
          faltantes: this.celdaNumero(contrato.faltantes),
          conveniosCapturados: this.celdaNumero(conveniosCapturados),
          estimacionesCapturadas: this.celdaNumero(estimacionesCapturadas),
        };

        this.getCatalogoOrdenado.forEach((documentoCatalogo) => {
          fila[this.claveColumnaCatalogo(documentoCatalogo)] = this.celdaCatalogo(
            this.getValorCeldaCatalogo(documentos, documentoCatalogo.id),
          );
        });

        return fila;
      });
    },
    filaTotales() {
      const fila = {
        fecha: this.celdaTotal('TOTALES'),
        numero: this.celdaTotal(''),
        objeto: this.celdaTotal(''),
        contratista: this.celdaTotal(''),
        responsable: this.celdaTotal(''),
        total: this.celdaTotal(this.$ObjSumar(this.resultados, 'totalDocumentos')),
        noAplicables: this.celdaTotal(this.$ObjSumar(this.resultados, 'noAplicables')),
        aplican: this.celdaTotal(this.$ObjSumar(this.resultados, 'documentosAplicables')),
        integrados: this.celdaTotal(this.$ObjSumar(this.resultados, 'integrados')),
        faltantes: this.celdaTotal(this.$ObjSumar(this.resultados, 'faltantes')),
        conveniosCapturados: this.celdaTotal(this.sumarCampoFilas('conveniosCapturados')),
        estimacionesCapturadas: this.celdaTotal(this.sumarCampoFilas('estimacionesCapturadas')),
      };

      this.getCatalogoOrdenado.forEach((documentoCatalogo) => {
        const clave = this.claveColumnaCatalogo(documentoCatalogo);
        fila[clave] = this.celdaTotal(this.sumarCampoFilas(clave));
      });

      return fila;
    },
    crearEncabezadosEtapa() {
      const encabezados = [];
      let columnaActual = COLUMNAS_FIJAS.length + 1;

      [1, 2, 3].forEach((etapa) => {
        const documentosEtapa = this.getCatalogoOrdenado
          .filter((documento) => documento.etapa === etapa);
        if (documentosEtapa.length === 0) return;

        const columnaFinal = columnaActual + documentosEtapa.length - 1;
        const encabezado = {
          value: NOMBRES_ETAPA[etapa] || `Etapa ${etapa}`,
          row: 2,
          col: columnaActual,
          estilo: {
            font: { size: 11, bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
          },
        };
        if (columnaFinal > columnaActual) {
          encabezado.combinar = { row: 2, col: columnaFinal };
        }
        encabezados.push(encabezado);
        columnaActual = columnaFinal + 1;
      });

      return encabezados;
    },
    crearDatosDocumento() {
      const rangoConsultado = `DEL ${this.$moment(this.filtros.fechaInicio).format('DD/MM/YYYY')} AL ${this.$moment(this.filtros.fechaFin).format('DD/MM/YYYY')}`;

      return [
        {
          value: `ESTATUS DE EXPEDIENTES - GENERADO EL ${new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase()} | RANGO: ${rangoConsultado}`,
          row: 1,
          col: 1,
          estilo: {
            font: { size: 14, bold: true },
            alignment: { horizontal: 'left', vertical: 'center' },
          },
          combinar: { row: 1, col: Math.max(this.totalColumnas, 12) },
        },
        ...this.crearEncabezadosEtapa,
      ];
    },
    crearExcelTableroExpedientes() {
      const bodyData = this.datosExportacion.length > 0
        ? [...this.filasDatos, this.filaTotales]
        : [{}];

      return {
        excelData: {
          configuracion: this.getConfiguracionExcel,
          header: {
            filaInicio: filaTitulos,
            estilo: this.estiloHeader,
            datosDocumento: [...this.crearDatosDocumento],
            data: [...this.getHeaders],
          },
          body: {
            nombreDeColumnas: this.getColumnas,
            filaInicio: filaInicioDatos,
            data: bodyData,
          },
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped></style>
